// https://stackoverflow.com/a/20988824
// this is a pattern that we reuse in other queries so we can't use the ^ and $ anchors
export const MONGO_OBJECTID_REGEX: RegExp = /[a-f\d]{24}/i;

// this ensures no characters but the mongo object id are included
export const STRICT_MONGO_OBJECTID_REGEX: RegExp = new RegExp(`^${MONGO_OBJECTID_REGEX.source}$`, 'i');

/**
 * Matches a Git commit hash (SHA-1).
 *
 * @example 1857349e7519b0b194b6a59a363056599b9dbeb6
 * @example bb9d1
 */
export const COMMIT_HASH_REGEX: RegExp = /\b[0-9a-f]{5,40}\b/;

/**
 * @example #fff
 * @example #6184a7
 */
const VALID_HEX_PATTERN = [3, 4, 6, 8].map(digitCount => `#[0-9a-fA-F]{${digitCount}}`).join('|');
export const HEX_COLOR: RegExp = new RegExp(`^(?:${VALID_HEX_PATTERN})$`, 'i');

/**
 * Matches `new Date().toISOString()` strings.
 *
 * @example 2023-04-03T06:30:05.973Z
 */
export const ISO_DATE_REGEX: RegExp = /\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d.\d\d\dZ/;

/**
 * A lenient semver regex that:
 * - Only requires a major number, but leaves minor and patch optional
 * - Allows an optional `-` pre-release suffix with _any_ set of characters
 *   afterwards, rather than those that the semver spec actually restricts.
 * - Does not include `^` and `$` operators to enforce that the entire string is
 *   valid, allowing it to match on substrings. This is needed because this
 *   regex's source is used to build up other combination regexes that look for
 *   semver substrings.
 *
 * @see SEMVER_REGEX_STRICT for a stricter regex without these caveats
 *
 * @example 1
 * @example 1.1
 * @example 1.1.1
 * @example 4-debug.test
 * @example 1.1_invalid-chars // this is not valid semver, but will pass validation because the substring `1.1` matches
 */
export const SEMVER_REGEX: RegExp = /([0-9]+)(?:\.([0-9]+))?(?:\.([0-9]+))?(-.*)?/;

/**
 * A "relaxed" semver regex to test whether an entire string is a valid semver
 * value according to the official spec.
 *
 * This regex is copied from semver.org, but makes the minor and patch groups
 * optional via the `?` operator so that we accept semver values like `1` and
 * `1.0` rather than requiring the full `1.0.0`.
 *
 * @link
 * https://semver.org/#is-there-a-suggested-regular-expression-regex-to-check-a-semver-string
 *
 * @example 1
 * @example 1.1
 * @example 1.1.1
 * @example 1.1.1-prerelease
 * @example 1.1.1+build
 * @example 01.01.01
 *
 */

export const SEMVER_REGEX_STRICT: RegExp =
  /^(\d+)(\.(\d+))?(\.(\d+))?(?:-((?:\d+|[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:\d+|[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

/**
 * Extension of SEMVER_REGEX_STRICT to allow for an optional `-xx` language suffix
 * for i18n support.
 *
 * @example 1
 * @example 1.1-en
 * @example 1.1.1-fr
 * @example 1.1.1-prerelease-es
 * @example 1.1.1+build-zh
 * @example 01.01.01-ja
 */
export const SEMVER_LANG_REGEX: RegExp =
  /^(\d+)(\.(\d+))?(\.(\d+))?(?:-((?:\d+|[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:\d+|[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?(-[a-z]{2})?$/;

/**
 * @example getting-started
 * @example Getting Started
 * @example côte-divoire
 */
export const SLUG_REGEX: RegExp = /([a-z0-9-_ ]|[^\\x00-\\x7F])+/i;

/**
 * @example 5bf509a0-f094-440b-b932-34616449a55b
 * @example c73bcdcc-2669-4bf6-81d3-e4ae73fb11fd
 * @example C73BCDCC-2669-4Bf6-81d3-E4AE73FB11FD
 *
 * @see {@link https://ihateregex.io/expr/uuid/}
 */
export const UUIDv4: RegExp = /[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/i;
